class ThirdPartyApps extends HTMLElement {
  #atcButtons = [];
  #visionAiWidgetLoaded = false;
  #handle = '';
  #modal = null;
  #a11y = null;
  #isAnimating = false;
  #currentModalButton = null;

  constructor() {
    super();

    this.#a11y = window.theme.a11y;
  }

  connectedCallback() {
    this.#modal = this.querySelector('[data-quick-add-modal]');
    
    this.detectVisionAiWidget();
  }

  disconnectedCallback() {
    this.detachEventListeners();
  }

  detachEventListeners() {
    this.#atcButtons.forEach((button, index) => {
      button.removeEventListener('click', button.boundClick);
      delete button.boundClick;
    });
  }

  detectVisionAiWidget() {
    const waitForVsaiLayout = new MutationObserver(
      (mutationsList, observer) => {
        this.#atcButtons = Array.from(this.querySelectorAll('.xsvai-item__add-to-cart'));
        
        if (this.#atcButtons.length > 0) {
          setTimeout(() => {
            if (this.#visionAiWidgetLoaded) return;

            this.#visionAiWidgetLoaded = true;
            observer.disconnect();

            this.#setupEventListeners();
            this.#updateProductPrices();
          }, 1000);
        }
      }
    );

    waitForVsaiLayout.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  #setupEventListeners() {
    this.#atcButtons.forEach((button, index) => {
      const xsvaiItem = button.closest('.xsvai-item');
      const productItem = xsvaiItem?.querySelector('.xsvai-item__thumbnail');

      const productHandle = getProductHandleFromURL(productItem?.getAttribute('href'));
      
      button.removeAttribute('onclick');

      button.boundClick = (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.createClickHandler(button, productHandle)(event);
      };
      button.addEventListener('click', button.boundClick);
    });
  }

  #updateProductPrices() {
    const priceItems = Array.from(this.querySelectorAll('.xsvai-item__price'));
    priceItems.forEach((priceItem, index) => {
      const regularPriceElem = priceItem.querySelector('span:first-child');
      const comparePriceElem = priceItem.querySelector('span:nth-child(2)');

      if (regularPriceElem.textContent.trim() !== '' && regularPriceElem.textContent.trim() !== 'undefined') {
        const priceInCent = parsePriceToCents(regularPriceElem.textContent);
        regularPriceElem.textContent = formatPrice(priceInCent, this.dataset.currencyCode, this.dataset.currencyCodeEnable === 'true');
      }

      if (comparePriceElem.textContent.trim() !== '' && comparePriceElem.textContent.trim() !== 'undefined') {
        const priceInCent = parsePriceToCents(comparePriceElem.textContent);
        comparePriceElem.textContent = formatPrice(priceInCent, this.dataset.currencyCode, this.dataset.currencyCodeEnable === 'true');
      }
    });
  }

  createClickHandler(button, productHandle) {
    return () => this.atcButtonClick(button, productHandle);
  }

  atcButtonClick(button, productHandle) {
    this.#currentModalButton = button;
    this.#handle = productHandle;

    this.renderModal();
  }

  renderModal() {
    window
      .fetch(
        `${window.theme.routes.root}products/${this.#handle}?section_id=api-product-upsell`
      )
      .then(this.upsellErrorsHandler)
      .then((response) => {
        return response.text();
      })
      .then((response) => {
        this.modalCreate(response);
      });
  }

  upsellErrorsHandler(response) {
    if (!response.ok) {
      return response.json().then(function (json) {
        const e = new FetchError({
          status: response.statusText,
          headers: response.headers,
          json: json,
        });
        throw e;
      });
    }
    return response;
  }

  modalCreate(response) {
    this.#modal.querySelector('[data-product-upsell-ajax]').innerHTML =
      new DOMParser()
        .parseFromString(response, "text/html")
        .querySelector('[data-api-content]').innerHTML;

    this.modalCreatedCallback();
  }

  modalCreatedCallback() {
    this.modalEvents();
    this.modalOpen();
  }

  modalEvents() {
    // Close button click event
    this.#modal
      .querySelector('[data-quick-add-modal-close]')
      ?.addEventListener('click', (e) => {
        e.preventDefault();
        this.modalClose();
      });

    // Close dialog on click outside content
    this.#modal.addEventListener('click', (event) => {
      if (event.target.nodeName === 'DIALOG' && event.type === 'click') {
        this.modalClose();
      }
    });

    // Close dialog on click ESC key pressed
    this.#modal.addEventListener('keydown', (event) => {
      if (event.code == 'Escape') {
        event.preventDefault();
        this.modalClose();
      }
    });

    this.#modal.addEventListener('theme:modal:close', () => {
      this.modalClose();
    });

    // Close dialog after animation completes
    this.#modal.addEventListener('animationend', (event) => {
      if (event.target !== this.#modal) return;
      this.#isAnimating = false;

      if (this.#modal.hasAttribute('closing')) {
        this.modalClose();
      } else {
        setTimeout(() => {
          this.#a11y.trapFocus(this.#modal);
          const focusTarget =
            this.#modal.querySelector('[autofocus]') ||
            this.#modal.querySelector('button, [href], select, textarea, [tabindex]:not([tabindex="-1"])');
          focusTarget?.focus();
        }, 50);
      }
    });
  }

  modalOpen() {
    if (typeof this.#modal.show === "function") {
      this.#modal.show();
    }

    this.#modal.setAttribute("open", true);
    this.#modal.removeAttribute("inert");

    if (this.#currentModalButton) {
      this.#currentModalButton.disabled = false;
      window.a11y.lastElement = this.#currentModalButton;
    }

    // Animate items
    requestAnimationFrame(() => {
      this.#modal.querySelectorAll('[data-animation]').forEach((item) => {
        item.classList.add('is-animated');
      });
    });

    document.dispatchEvent(
      new CustomEvent("theme:quick-add:open", { bubbles: true })
    );
    document.dispatchEvent(
      new CustomEvent("theme:scroll:lock", { bubbles: true })
    );
    document.addEventListener(
      "theme:product:added",
      this.modalCloseOnProductAdded,
      { once: true }
    );
  }

  modalClose() {
    if (this.#isAnimating) {
      return;
    }

    if (!this.#modal.hasAttribute('closing')) {
      this.#modal.setAttribute('closing', '');
      this.#isAnimating = true;
      return;
    }

    // Check if browser supports Dialog tags
    if (typeof this.#modal.close === 'function') {
      this.#modal.close();
    } else {
      this.#modal.removeAttribute('open');
    }

    this.#modal.removeAttribute('closing');
    this.#modal.setAttribute("inert", "");
    this.#modal.classList.remove('is-loading');

    if (this.#currentModalButton) {
      this.#currentModalButton.disabled = false;
    }

    // Unlock scroll if no other drawers & modals are open
    if (!window.theme.hasOpenModals()) {
      document.dispatchEvent(
        new CustomEvent("theme:scroll:unlock", { bubbles: true })
      );
    }

    document.removeEventListener(
      "theme:product:added",
      this.modalCloseOnProductAdded
    );

    this.#a11y.removeTrapFocus();
    this.#a11y.autoFocusLastElement();
  }

  modalCloseOnProductAdded() {
    this.resetQuickAddButtons();
    if (this.#modal && this.#modal.hasAttribute('open')) {
      this.modalClose();
    }
  }

  resetQuickAddButtons() {
    if (this.#currentModalButton) {
      this.#currentModalButton.disabled = false;
    }
  }
}

function FetchError(object) {
  this.status = object.status || null;
  this.headers = object.headers || null;
  this.json = object.json || null;
  this.body = object.body || null;
}
FetchError.prototype = Error.prototype;

function getProductHandleFromURL(url) {
  const match = url.match(/\/products\/([^/?#]+)/);
  return match ? match[1] : null;
}

function parsePriceToCents(priceStr) {
  if (!priceStr) return null;

  // Remove currency symbols and letters
  let normalized = priceStr
    .replace(/[^\d,.\s]/g, "")  // Remove €, EUR, and other non-numeric characters except dots and commas
    .trim()
    .replace(/\s/g, "");        // Remove spaces

  // Convert comma to dot if it appears as decimal separator
  if (normalized.includes(",")) {
    // If both , and . exist, assume , is thousands separator (e.g., "1.200,00")
    if (normalized.includes(".")) {
      normalized = normalized.replace(/\./g, "").replace(",", ".");
    } else {
      normalized = normalized.replace(",", ".");
    }
  }

  const value = parseFloat(normalized);

  if (isNaN(value)) return null;

  return Math.round(value * 100);
}

function formatPrice(priceInCents, currencyCode, currencyCodeEnable) {
  const price = (priceInCents / 100).toFixed(2).replace('.', ',');
  return currencyCodeEnable ? `${currencyCode}${price}` : `${price} ${currencyCode}`;
}

if (!customElements.get('third-party-apps'))
  customElements.define('third-party-apps', ThirdPartyApps);
